import PropTypes from 'prop-types';
import responsiveProps from './responsiveProps';
import variantProp from './variantProp';

/**
 * @typedef {0|1|2|3|4|5|6|7|8|9|10|11} SpacingIndex - value used to select a size on the spacing scale
 *
 * @typedef SpacingOptions
 * @property {VariantProp} [SpacingOptions.variant] - optional theme scale variants e.g. compact, wide
 * @property {number} [SpacingOptions.size] - optional override to force a particular pixel size
 * @property {number} [SpacingOptions.subtract] - optional number to subtract from final pixel size
 *
 * @typedef SpacingObject
 * @property {SpacingIndex} [SpacingObject.xs] - space scale index to use above xs viewport
 * @property {SpacingIndex} [SpacingObject.sm] - space scale index to use above sm viewport
 * @property {SpacingIndex} [SpacingObject.md] - space scale index to use above md viewport
 * @property {SpacingIndex} [SpacingObject.lg] - space scale index to use above lg viewport
 * @property {SpacingIndex} [SpacingObject.xl] - space scale index to use above xl viewport
 * @property {SpacingIndex} [SpacingObject.space] - space scale index to use at all viewports
 * @property {SpacingOptions} [SpacingObject.options] - optional options for this spacing
 *
 * @typedef {SpacingIndex|SpacingObject} SpacingValue
 */
const spacingScale = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const spacingIndexPropType = PropTypes.oneOf(spacingScale);
const spacingObjectPropType = PropTypes.shape({
  ...responsiveProps.getByViewport(spacingIndexPropType),
  space: spacingIndexPropType,
  options: PropTypes.shape({
    variant: variantProp.propType,
    size: PropTypes.number
  })
});

/**
 * Components and utilities that assign fixed space between components use a theme-defined spacing scale.
 *
 * They typically take one or more props of the {@link SpacingValue} type and turn it into a pixel size value
 * using the hook `useSpacingScale`, which resolves any options or responsive behaviour and returns the
 * appropriate value from the theme spacing scale.
 *
 * - see /ADRs/inter-component-spacing/README.md - ADR on this structure
 * - see /src/utils/spacing/useSpacingScale.js - hook that processes spacing values
 * - @see {@link SpacingIndex} - themes provide spacing scales of up to 12 sizes with optional theme rules.
 * - @see {@link SpacingValue} - either a simple number referencing an index position on the spacing
 *   scale, or an object with an optional `options` key and one or more spacing indexes keyed either by
 *   viewports or `space` to apply at all viewports.
 */
export default {
  scale: spacingScale,
  types: {
    spacingIndex: spacingIndexPropType,
    spacingObject: spacingObjectPropType,
    // Most spacing components and utilities take this prop / arg type:
    spacingValue: PropTypes.oneOfType([spacingIndexPropType, spacingObjectPropType])
  }
};